/**
 * MODULE: domain/sales/frontend/components/accounts/stakeholderRisk.ts
 * PURPOSE: Risk-aware stakeholder map helpers — turn buyer-graph attributes (champion/detractor,
 *          relationship strength, sentiment, last engagement) into display config + a
 *          "champion gone quiet" signal for the Align Team org chart.
 * PUBLIC API: BUYING_ROLE_CONFIG, RELATIONSHIP_STRENGTH_CONFIG, SENTIMENT_CONFIG,
 *             QUIET_DAYS, daysSince, isStakeholderQuiet, getStakeholderRisk, getRiskBorderClass,
 *             hasStakeholderRisk
 * SIDE EFFECTS: None (pure)
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: ~150 / 200 lines
 * RECENT: 2026-07-07 [2.2.1521] — New: buyer graph as a risk signal.
 */

import type {
  BuyingRole,
  Contact,
  RelationshipStrength,
  StakeholderSentiment,
} from '../../../contracts/sales.types';

/** A key stakeholder (champion/decision maker) with no engagement in this many days is "gone quiet". */
export const QUIET_DAYS = 21;

type RoleTone = 'good' | 'bad' | 'neutral';

export const BUYING_ROLE_CONFIG: Record<
  BuyingRole,
  { label: string; short: string; badgeClass: string; borderClass: string; tone: RoleTone }
> = {
  champion: {
    label: 'Champion',
    short: 'CH',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
    borderClass: 'border-green-500 dark:border-green-400',
    tone: 'good',
  },
  decision_maker: {
    label: 'Decision maker',
    short: 'DM',
    badgeClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
    borderClass: 'border-indigo-500 dark:border-indigo-400',
    tone: 'neutral',
  },
  influencer: {
    label: 'Influencer',
    short: 'IN',
    badgeClass: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200',
    borderClass: 'border-cyan-500 dark:border-cyan-400',
    tone: 'neutral',
  },
  blocker: {
    label: 'Blocker',
    short: 'BL',
    badgeClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
    borderClass: 'border-orange-500 dark:border-orange-400',
    tone: 'bad',
  },
  detractor: {
    label: 'Detractor',
    short: 'DE',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
    borderClass: 'border-red-500 dark:border-red-400',
    tone: 'bad',
  },
  neutral: {
    label: 'Neutral',
    short: 'NE',
    badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    borderClass: 'border-gray-300 dark:border-gray-600',
    tone: 'neutral',
  },
};

export const RELATIONSHIP_STRENGTH_CONFIG: Record<
  RelationshipStrength,
  { label: string; dots: number; dotClass: string }
> = {
  strong: { label: 'Strong relationship', dots: 3, dotClass: 'bg-green-500' },
  moderate: { label: 'Moderate relationship', dots: 2, dotClass: 'bg-amber-500' },
  weak: { label: 'Weak relationship', dots: 1, dotClass: 'bg-red-500' },
};

export const SENTIMENT_CONFIG: Record<
  StakeholderSentiment,
  { label: string; symbol: string; className: string }
> = {
  positive: { label: 'Positive sentiment', symbol: '▲', className: 'text-green-600 dark:text-green-400' },
  neutral: { label: 'Neutral sentiment', symbol: '＝', className: 'text-gray-500 dark:text-gray-400' },
  negative: { label: 'Negative sentiment', symbol: '▼', className: 'text-red-600 dark:text-red-400' },
};

/** Whole days elapsed since an ISO timestamp; null when the input is missing/invalid. */
export function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

/**
 * "Champion gone quiet": a key stakeholder (champion or decision maker) whose last engagement is
 * stale (>= QUIET_DAYS). We only flag when we HAVE a stale timestamp — a missing timestamp means
 * "not tracked yet", so we don't raise false alarms on freshly added contacts.
 */
export function isStakeholderQuiet(contact: Pick<Contact, 'buying_role' | 'last_engaged_at'>): boolean {
  if (contact.buying_role !== 'champion' && contact.buying_role !== 'decision_maker') return false;
  const days = daysSince(contact.last_engaged_at);
  return days != null && days >= QUIET_DAYS;
}

export type StakeholderRisk = {
  role: BuyingRole | null;
  roleConfig: (typeof BUYING_ROLE_CONFIG)[BuyingRole] | null;
  strength: RelationshipStrength | null;
  sentiment: StakeholderSentiment | null;
  quiet: boolean;
  quietDays: number | null;
  hasAny: boolean;
};

/** Normalize a contact's risk attributes into a render-ready descriptor. */
export function getStakeholderRisk(
  contact: Pick<Contact, 'buying_role' | 'relationship_strength' | 'sentiment' | 'last_engaged_at'>
): StakeholderRisk {
  const role = contact.buying_role ?? null;
  const quiet = isStakeholderQuiet(contact);
  return {
    role,
    roleConfig: role ? BUYING_ROLE_CONFIG[role] : null,
    strength: contact.relationship_strength ?? null,
    sentiment: contact.sentiment ?? null,
    quiet,
    quietDays: quiet ? daysSince(contact.last_engaged_at) : null,
    hasAny: Boolean(role || contact.relationship_strength || contact.sentiment),
  };
}

export function hasStakeholderRisk(
  contact: Pick<Contact, 'buying_role' | 'relationship_strength' | 'sentiment' | 'last_engaged_at'>
): boolean {
  return getStakeholderRisk(contact).hasAny;
}

/**
 * Card border for a contact node. A "gone quiet" key stakeholder gets an amber alert border
 * regardless of role; otherwise the role's own tone border; else fall back to the default.
 */
export function getRiskBorderClass(
  contact: Pick<Contact, 'buying_role' | 'relationship_strength' | 'sentiment' | 'last_engaged_at'>,
  fallback: string
): string {
  const risk = getStakeholderRisk(contact);
  if (risk.quiet) return 'border-amber-500 dark:border-amber-400';
  if (risk.roleConfig) return risk.roleConfig.borderClass;
  return fallback;
}
